import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { RootState } from '../../redux/store'
import { devTiming } from '../../utils/devTiming'
import { publishSessionDiagnostics } from '../../utils/sessionDiagnostics'
import { environment } from '../../environments/environment'

const IS_DEV = process.env.NODE_ENV !== 'production'

/**
 * Cross-tab activity clock (RD 2026-08-07).
 *
 * The app opens its own routes in new tabs — the Record page via `window.open(..., '_blank')`
 * in detail-container, lab PDFs in history-pdi. Each tab keeps its idle clock in an
 * in-memory ref, so working in the Record tab for twenty minutes did nothing for the chart
 * tab's clock: the clinician was using the application continuously and still returned to
 * an instant logout.
 *
 * That is a FALSE POSITIVE in the control, not a HIPAA requirement. §164.312(a)(2)(iii)
 * targets an UNATTENDED workstation; a human actively working in another tab of the same
 * app is attended. Sharing the timestamp through localStorage makes the control more
 * accurate, not weaker — when every tab really is idle nothing is written and all of them
 * time out exactly as before.
 *
 * localStorage rather than BroadcastChannel because the value must survive a tab being
 * frozen or discarded: a returning tab reads the last known timestamp directly instead of
 * relying on having received an event while it was throttled.
 */
const SHARED_ACTIVITY_KEY = 'atman.session.lastActivity'

const readSharedActivity = (): number => {
  try {
    const raw = window.localStorage.getItem(SHARED_ACTIVITY_KEY)
    const parsed = raw ? Number(raw) : NaN
    if (!Number.isFinite(parsed) || parsed <= 0) return 0
    // IGNORE a future timestamp — do not clamp it to now. Clamping is equivalent to
    // treating it as fresh activity, so a skewed or tampered value would hold the session
    // open indefinitely. Sibling tabs share one machine clock, so a future value is never
    // legitimate. (Caught by the future-timestamp negative control.)
    if (parsed > Date.now()) return 0
    return parsed
  } catch {
    return 0
  }
}

const writeSharedActivity = (timestamp: number): void => {
  try {
    window.localStorage.setItem(SHARED_ACTIVITY_KEY, String(timestamp))
  } catch {
    // Private mode / quota — degrade to per-tab behaviour rather than throwing.
  }
}

/**
 * The HIPAA idle auto-logoff warning, decoupled from any one auth provider.
 *
 * WHY THIS EXISTS AS ITS OWN COMPONENT (CAPA F273):
 * The countdown dialog used to live inside `OktaTokenRenewal`, which App.tsx mounts
 * only under `okta.isEnabled && !oidc.isEnabled`. Both flags shipped defaulting to the
 * OPPOSITE of that pair, so under stock configuration the warning was structurally
 * unreachable — a clinician's session ended with no dialog and no chance to stay, and
 * the OIDC branch never had one at all. Nothing was reverted; the control simply could
 * not mount.
 *
 * A HIPAA §164.312(a)(2)(iii) control measure must not be a child of one branch of an
 * auth feature flag. Idle time is idle time regardless of who issued the token, so idle
 * detection, the warning, and the countdown live here and are mounted ONCE at the app
 * root, outside every auth branch. Each auth path contributes only the two things that
 * genuinely differ — how to extend a session and how to end one — by registering a
 * `SessionAuthAdapter`.
 *
 * Adding a new auth path therefore cannot silently remove the warning: the guard is
 * already mounted, and the new path supplies an adapter or gets no session management
 * at all (which `App.auth-branch.test.tsx` pins per branch).
 */
export interface SessionAuthAdapter {
  /** Whether a session is currently established. The guard is inert when false. */
  isAuthenticated: boolean
  /** Extend the session in response to "Stay Logged In". */
  extend: () => Promise<void> | void
  /** End the session — countdown expiry, "Log Out Now", or a stale tab-return. */
  logout: () => Promise<void> | void
  /** Provider name, for logs only. */
  label: string
}

/** Registration seam: an auth branch publishes its adapter to the root-mounted guard. */
const SessionAuthRegistryContext = createContext<
  ((adapter: SessionAuthAdapter | null) => void) | null
>(null)

export interface SessionActivity {
  /** Stamp "the user is here now" — e.g. after a successful token renewal. */
  markActivity: () => void
  /** Whether the user has interacted inside the inactivity threshold. */
  isUserActive: () => boolean
}

const noopActivity: SessionActivity = {
  markActivity: () => {},
  isUserActive: () => true,
}

const SessionActivityContext = createContext<SessionActivity>(noopActivity)

/**
 * Publish this auth branch's session adapter to the guard. Safe to call when no guard
 * is mounted (tests rendering a branch in isolation) — it simply does nothing.
 */
export const useRegisterSessionAuth = (adapter: SessionAuthAdapter): void => {
  const register = useContext(SessionAuthRegistryContext)
  const { isAuthenticated, extend, logout, label } = adapter

  useEffect(() => {
    if (!register) {
      return
    }
    register({ isAuthenticated, extend, logout, label })
    return () => register(null)
  }, [register, isAuthenticated, extend, logout, label])
}

/** Read shared activity state. Auth providers use this to skip renewal while idle. */
export const useSessionActivity = (): SessionActivity => useContext(SessionActivityContext)

const formatTime = (ms: number) => {
  const seconds = Math.floor(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  if (hours > 0) return `${hours}h ${minutes % 60}m ${seconds % 60}s`
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`
  return `${seconds}s`
}

interface SessionInactivityGuardProps {
  children: React.ReactNode
  /** Idle time before the warning appears. HIPAA guidance for PHI systems is 15 min. */
  inactivityThreshold?: number
  /** How long the countdown runs before auto-logout. */
  warningDuration?: number
  /** How often idle state is evaluated. */
  checkInterval?: number
}

const SessionInactivityGuard: React.FC<SessionInactivityGuardProps> = ({
  children,
  inactivityThreshold: inactivityThresholdProp = 900000,
  warningDuration: warningDurationProp = 60000,
  checkInterval: checkIntervalProp = 60000,
}) => {
  // Dev-only: exercise the idle path in seconds rather than 15 minutes, without editing
  // a HIPAA threshold that then has to be remembered back out (see utils/devTiming.ts).
  // Inert in production builds — the prop defaults above are the only values that can
  // reach a deployed environment.
  const inactivityThreshold = devTiming('inactivityThresholdMs', inactivityThresholdProp)
  const warningDuration = devTiming('warningDurationMs', warningDurationProp)
  const checkInterval = devTiming('renewalCheckIntervalMs', checkIntervalProp)

  const [adapter, setAdapter] = useState<SessionAuthAdapter | null>(null)
  const adapterRef = useRef<SessionAuthAdapter | null>(null)
  adapterRef.current = adapter

  // An in-progress AI-Scribe recording is an *attended* session — HIPAA auto-logoff
  // targets unattended workstations. Without this a clinician talking through an
  // encounter (no mouse/keyboard) is counted idle and logged out mid-recording,
  // destroying the in-memory audio before it uploads (CAPA F131).
  const recordingActive = useSelector((s: RootState) => s.recording.isRecording)
  const recordingActiveRef = useRef<boolean>(recordingActive)
  recordingActiveRef.current = recordingActive

  /**
   * The EFFECTIVE idle clock: the most recent input across every app tab. This is what the
   * timeout is measured against, and `syncSharedActivity` raises it toward a sibling's
   * activity — so it is deliberately NOT a record of what happened in this tab.
   */
  const lastActivityRef = useRef<number>(Date.now())

  /**
   * Input observed in THIS tab specifically. Only `markActivity` ever writes it, and
   * `syncSharedActivity` never touches it.
   *
   * Kept separate because the two were conflated (2026-08-07): the probe reported
   * `thisTabLastActiveSecondsAgo` from `lastActivityRef`, which sibling activity overwrites,
   * so both fields collapsed to the same number exactly when they most needed to differ.
   * A stale background tab — the failure mode the probe exists to identify — is precisely
   * "no local input for a long time, while some other tab is active", and that is
   * unobservable if the local figure silently tracks the sibling.
   *
   * `null` until this tab sees its first input, which is honest: a tab opened by a click in
   * ANOTHER tab has genuinely never been interacted with.
   */
  const localInputAtRef = useRef<number | null>(null)

  const warningShownRef = useRef<boolean>(false)
  const showWarningRef = useRef<boolean>(false)
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null)
  const checkTimerRef = useRef<NodeJS.Timeout | null>(null)

  const [showWarning, setShowWarning] = useState(false)
  const [countdown, setCountdown] = useState(Math.floor(warningDuration / 1000))
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  const markActivity = useCallback(() => {
    const now = Date.now()
    lastActivityRef.current = now
    // The ONLY writer of the local-input record. Sibling sync must never touch this.
    localInputAtRef.current = now
    // Publish to sibling app tabs — see SHARED_ACTIVITY_KEY.
    writeSharedActivity(now)
  }, [])

  /** Adopt a sibling tab's more-recent activity. Never moves the clock backwards. */
  const syncSharedActivity = useCallback(() => {
    const shared = readSharedActivity()
    if (shared > lastActivityRef.current) {
      lastActivityRef.current = shared
    }
  }, [])

  const isUserActive = useCallback(
    () => Date.now() - lastActivityRef.current < inactivityThreshold,
    [inactivityThreshold]
  )

  const clearCountdown = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current)
      countdownTimerRef.current = null
    }
  }, [])

  const handleLogout = useCallback(async () => {
    const active = adapterRef.current
    if (!active) {
      return
    }
    setIsLoggingOut(true)
    IS_DEV && console.log(`[SessionInactivityGuard] Logging out (${active.label}) due to inactivity...`)
    try {
      await active.logout()
    } catch (error) {
      console.error('[SessionInactivityGuard] Error during logout:', error)
      // Logout failed, so this component stays mounted. Release the flag before
      // redirecting: both dialog buttons are disabled while it is set, and a logout
      // attempt that never navigates would otherwise leave the next warning dead.
      setIsLoggingOut(false)
      window.location.href = '/'
    }
  }, [])

  const handleStayLoggedIn = useCallback(() => {
    IS_DEV && console.log('[SessionInactivityGuard] User clicked Stay Logged In')

    // Dismiss FIRST, extend after. Awaiting the round-trip before clearing the warning
    // kept the dialog on screen for the whole renewal, which reads as a dead button —
    // and worse, the countdown was still running, so a renewal slower than the
    // remaining countdown fired the countdown-zero logout and signed the user out
    // despite them asking to stay. The click is unambiguous intent and is itself the
    // activity we care about, so the dialog closes immediately.
    lastActivityRef.current = Date.now()
    setIsLoggingOut(false)
    warningShownRef.current = false
    showWarningRef.current = false
    setShowWarning(false)
    setCountdown(Math.floor(warningDuration / 1000))
    clearCountdown()

    const active = adapterRef.current
    if (!active) {
      return
    }
    void (async () => {
      try {
        await active.extend()
        // Re-stamp on completion: the round-trip can take seconds and the idle window
        // should run from when the session was actually extended, not from the click.
        lastActivityRef.current = Date.now()
        IS_DEV && console.log('[SessionInactivityGuard] Session extended on Stay Logged In')
      } catch (error) {
        console.error('[SessionInactivityGuard] Extend on Stay Logged In failed:', error)
      }
    })()
  }, [warningDuration, clearCountdown])

  const showWarningDialog = useCallback(() => {
    if (warningShownRef.current || showWarningRef.current) {
      return
    }
    IS_DEV && console.warn('[SessionInactivityGuard] Showing inactivity warning dialog')
    warningShownRef.current = true
    showWarningRef.current = true
    setShowWarning(true)
    setCountdown(Math.floor(warningDuration / 1000))
    // A background logout attempt may have set this and never cleared it. The dialog
    // must always open with its buttons live.
    setIsLoggingOut(false)

    // The updater stays PURE — logout fires from the effect below. React 18 StrictMode
    // double-invokes state updaters, so a logout call in here ran twice and queued
    // state updates during another component's render.
    countdownTimerRef.current = setInterval(() => {
      setCountdown(prev => Math.max(prev - 1, 0))
    }, 1000)
  }, [warningDuration])

  const checkInactivity = useCallback(() => {
    if (!adapterRef.current?.isAuthenticated) {
      return
    }
    // An active recording counts as activity: never auto-logoff (or even warn)
    // mid-encounter. Bumping lastActivityRef also keeps the auth provider's renewal
    // alive through a long silent recording, so the eventual upload does not 401
    // on an expired access token (CAPA F131).
    if (recordingActiveRef.current) {
      lastActivityRef.current = Date.now()
      return
    }
    // Belt-and-braces alongside the `storage` listener: a missed event must not be able to
    // idle out a tab whose sibling is actively in use.
    syncSharedActivity()

    if (Date.now() - lastActivityRef.current >= inactivityThreshold) {
      if (!warningShownRef.current && !showWarningRef.current) {
        showWarningDialog()
      }
    } else if (warningShownRef.current && !showWarningRef.current) {
      warningShownRef.current = false
    }
  }, [inactivityThreshold, showWarningDialog, syncSharedActivity])

  // Countdown reached zero while the warning was up — log out. This lives in an effect
  // rather than inside the setCountdown updater so it fires exactly once (StrictMode
  // double-invokes updaters, which double-fired logout).
  useEffect(() => {
    if (!showWarning || countdown > 0) {
      return
    }
    clearCountdown()
    handleLogout()
  }, [showWarning, countdown, handleLogout, clearCountdown])

  // Activity listeners
  useEffect(() => {
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click']
    let throttleTimer: NodeJS.Timeout | null = null
    const throttledUpdate = () => {
      if (!throttleTimer) {
        markActivity()
        throttleTimer = setTimeout(() => {
          throttleTimer = null
        }, 5000)
      }
    }
    activityEvents.forEach(event => {
      window.addEventListener(event, throttledUpdate, { passive: true })
    })
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, throttledUpdate)
      })
      if (throttleTimer) {
        clearTimeout(throttleTimer)
      }
    }
  }, [markActivity])

  // Periodic idle evaluation
  useEffect(() => {
    if (!adapter?.isAuthenticated) {
      return
    }
    checkTimerRef.current = setInterval(checkInactivity, checkInterval)
    return () => {
      if (checkTimerRef.current) {
        clearInterval(checkTimerRef.current)
        checkTimerRef.current = null
      }
    }
  }, [adapter?.isAuthenticated, checkInactivity, checkInterval])

  // Re-check on tab return. A background tab has its setInterval throttled or frozen, so
  // the periodic check cannot warn while the user is away — by the time they come back the
  // threshold may already have been crossed.
  //
  // It used to log out IMMEDIATELY here (f3e57392). RD 2026-08-07 disposed that: show the
  // countdown instead, so a returning clinician gets the same chance to stay signed in that
  // every other path gives them. Auto-logoff is preserved — ignoring the countdown still
  // ends the session; it is now an interaction step rather than a silent teardown.
  //
  // The sibling-tab sync runs FIRST and matters more than the countdown: the app opens its
  // own Record page and lab PDFs via window.open, so "another tab" is frequently this same
  // application. If a sibling tab has been active, this tab was never idle at all and must
  // not warn.
  useEffect(() => {
    if (!adapter?.isAuthenticated) {
      return
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return
      }
      if (recordingActiveRef.current) {
        lastActivityRef.current = Date.now()
        return
      }
      // Read the shared clock directly rather than trusting that storage events arrived
      // while this tab was throttled or frozen.
      syncSharedActivity()

      const idleFor = Date.now() - lastActivityRef.current
      if (idleFor >= inactivityThreshold) {
        IS_DEV &&
          console.warn(
            `[SessionInactivityGuard] Tab visible after ${formatTime(idleFor)} inactivity ` +
              `(threshold: ${formatTime(inactivityThreshold)}) — showing the countdown`
          )
        showWarningDialog()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [adapter?.isAuthenticated, inactivityThreshold, showWarningDialog, syncSharedActivity])

  // Adopt activity from sibling app tabs as it happens, so a tab that stays visible on a
  // second monitor is also kept alive by work happening in the Record tab.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== SHARED_ACTIVITY_KEY) {
        return
      }
      syncSharedActivity()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [syncSharedActivity])

  useEffect(() => () => clearCountdown(), [clearCountdown])

  // Report what actually resolved, so "is the HIPAA countdown armed on this deployment?"
  // is answerable from the browser rather than from a config store nobody on the clinical
  // side can read (RD 2026-08-06). Diagnostics only — see utils/sessionDiagnostics.ts for
  // the non-sensitive contract this payload is bound by.
  useEffect(() => {
    publishSessionDiagnostics({
      authProvider: environment?.oidc?.isEnabled
        ? 'oidc'
        : environment?.okta?.isEnabled
          ? 'okta'
          : 'none',
      guardMounted: true,
      adapterRegistered: adapter !== null,
      adapterLabel: adapter?.label ?? null,
      authenticated: !!adapter?.isAuthenticated,
      inactivityThresholdMs: inactivityThreshold,
      warningDurationMs: warningDuration,
    })
    return () => publishSessionDiagnostics({ guardMounted: false })
  }, [adapter, inactivityThreshold, warningDuration])

  /**
   * Live idle probe: `window.__atmanSessionIdle()` in any tab's console.
   *
   * The idle clock is a ref, so until now there was no way to ask a tab how close it was
   * to timing out — you waited for the dialog or you guessed. That is what made the
   * background-tab logout so hard to pin down: the tab doing the counting is not the tab
   * you are looking at, and it could not be interrogated.
   *
   * Deliberately a SEPARATE global rather than a field on `window.__atmanSession`: that
   * object is a locked, all-primitive payload with a test asserting no functions or nested
   * values, and a snapshot field would go stale between publishes anyway. This reads the
   * ref live, per tab.
   *
   * Non-sensitive — elapsed seconds and the two thresholds, no identifiers.
   */
  useEffect(() => {
    const probe = () => {
      const shared = readSharedActivity()
      const effectiveLast = Math.max(lastActivityRef.current, shared)
      const idleMs = Date.now() - effectiveLast
      return {
        idleSeconds: Math.round(idleMs / 1000),
        secondsUntilWarning: Math.max(0, Math.round((inactivityThreshold - idleMs) / 1000)),
        secondsUntilLogout: Math.max(
          0,
          Math.round((inactivityThreshold + warningDuration - idleMs) / 1000)
        ),
        // THIS tab's own input only — null if it has never been interacted with.
        thisTabLastActiveSecondsAgo:
          localInputAtRef.current === null
            ? null
            : Math.round((Date.now() - localInputAtRef.current) / 1000),
        // Most recent input across ALL app tabs.
        anyTabLastActiveSecondsAgo: shared ? Math.round((Date.now() - shared) / 1000) : null,
        /**
         * THE DIAGNOSTIC: true when this tab has been untouched past the threshold while
         * some other tab is active. On a pre-fix bundle that is the tab about to call
         * signOut() and take the whole session down with it.
         */
        staleBackgroundTab:
          (localInputAtRef.current === null ||
            Date.now() - localInputAtRef.current >= inactivityThreshold) &&
          shared > 0 &&
          Date.now() - shared < inactivityThreshold,
        warningShowing: showWarningRef.current,
        recordingActive: recordingActiveRef.current,
        authenticated: !!adapter?.isAuthenticated,
      }
    }
    try {
      ;(window as any).__atmanSessionIdle = probe
    } catch {
      /* non-browser context */
    }
    return () => {
      try {
        delete (window as any).__atmanSessionIdle
      } catch {
        /* ignore */
      }
    }
  }, [adapter, inactivityThreshold, warningDuration])

  const activityValue = useMemo<SessionActivity>(
    () => ({ markActivity, isUserActive }),
    [markActivity, isUserActive]
  )

  return (
    <SessionAuthRegistryContext.Provider value={setAdapter}>
      <SessionActivityContext.Provider value={activityValue}>
        {children}

        <Dialog
          open={showWarning}
          onClose={() => {}}
          disableEscapeKeyDown
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderTop: '4px solid #ed6c02' } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberIcon color="warning" />
            <Typography variant="h6" component="span">
              Session Timeout Warning
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Your session is about to expire due to inactivity.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 3 }}>
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress
                  variant="determinate"
                  value={(countdown / (warningDuration / 1000)) * 100}
                  size={80}
                  thickness={4}
                  color="warning"
                />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="h4" component="div" color="text.secondary">
                    {countdown}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" align="center">
              You will be logged out in {countdown} second{countdown !== 1 ? 's' : ''}.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              onClick={handleStayLoggedIn}
              disabled={isLoggingOut}
              sx={{ minWidth: 150 }}
            >
              {isLoggingOut ? 'Logging out...' : 'Stay Logged In'}
            </Button>
            <Button
              variant="contained"
              color="warning"
              size="large"
              onClick={handleLogout}
              disabled={isLoggingOut}
            >
              Log Out Now
            </Button>
          </DialogActions>
        </Dialog>
      </SessionActivityContext.Provider>
    </SessionAuthRegistryContext.Provider>
  )
}

export default SessionInactivityGuard
